/**
 * Composer telemetry vocabulary. Every value sent to Hydro comes from one of
 * these lists so the `analytics.v0.BrowserEvent` context stays queryable by
 * exact match. Add values; never rename or repurpose existing ones.
 */

export const COMPOSER_TELEMETRY_SCHEMA_VERSION = 1

export const COMPOSER_EVENTS = {
  interaction: 'copilot_composer.interaction',
  sessionEnd: 'copilot_composer.session_end',
} as const
export type ComposerEventName = (typeof COMPOSER_EVENTS)[keyof typeof COMPOSER_EVENTS]

/**
 * Where the new-session composer is mounted. Passed explicitly by each host:
 * `immersive` is github.com/copilot, `dashboard` is the home dashboard input,
 * and `embedded` is the contextual Copilot panel opened from global navigation.
 */
export const COMPOSER_SURFACES = ['immersive', 'dashboard', 'embedded', 'unknown'] as const
export type ComposerSurface = (typeof COMPOSER_SURFACES)[number]

/** The page the `embedded` composer is attached to. Other surfaces report `none`. */
export const COMPOSER_CONTEXT_TYPES = [
  'none',
  'repository',
  'pull_request',
  'issue',
  'file',
  'discussion',
  'other',
] as const
export type ComposerContextType = (typeof COMPOSER_CONTEXT_TYPES)[number]

export const COMPOSER_ACTIONS = [
  'shown',
  'focused',
  'first_input',
  'paste',
  'cleared',
  'model_picker_opened',
  'model_changed',
  'model_picker_dismissed',
  'reasoning_effort_picker_opened',
  'reasoning_effort_changed',
  'reasoning_effort_picker_dismissed',
  'scope_changed',
  'agent_changed',
  'mode_changed',
  'approval_mode_changed',
  'repository_changed',
  'branch_changed',
  'environment_changed',
  'attachment_added',
  'attachment_removed',
  'space_selected',
  'space_cleared',
  'suggestion_selected',
  'submit_blocked',
] as const
export type ComposerAction = (typeof COMPOSER_ACTIONS)[number]

export const COMPOSER_SUBMIT_METHODS = [
  'enter',
  'modifier_enter',
  'button',
  'suggestion',
  'auto_submit',
  'unknown',
] as const
export type ComposerSubmitMethod = (typeof COMPOSER_SUBMIT_METHODS)[number]

export const COMPOSER_SESSION_OUTCOMES = ['submitted', 'abandoned'] as const
export type ComposerSessionOutcome = (typeof COMPOSER_SESSION_OUTCOMES)[number]

export const COMPOSER_ABANDON_TRIGGERS = ['unmount', 'pagehide'] as const
export type ComposerAbandonTrigger = (typeof COMPOSER_ABANDON_TRIGGERS)[number]

export const COMPOSER_ATTACHMENT_TYPES = [
  'image',
  'file',
  'folder',
  'repository',
  'commit',
  'discussion',
  'diff',
  'release',
  'snippet',
  'link',
  'workflow',
  'issue',
  'pull_request',
  'space',
  'unknown',
] as const
export type ComposerAttachmentType = (typeof COMPOSER_ATTACHMENT_TYPES)[number]

/** How an attachment reached the composer: chosen by the user, added from page context, or injected by the host. */
export const COMPOSER_ATTACHMENT_ORIGINS = ['user', 'implicit', 'injected'] as const
export type ComposerAttachmentOrigin = (typeof COMPOSER_ATTACHMENT_ORIGINS)[number]

/** Which control added a user attachment. */
export const COMPOSER_ATTACHMENT_VIAS = [
  'attach_menu',
  'image_button',
  'paste_or_drop',
  'mention',
  'space_picker',
  'unknown',
] as const
export type ComposerAttachmentVia = (typeof COMPOSER_ATTACHMENT_VIAS)[number]

/** Custom agents are user-named, so only their kind is logged; top-level agents also log their public login. */
export const COMPOSER_AGENT_KINDS = ['default', 'top_level', 'custom'] as const
export type ComposerAgentKind = (typeof COMPOSER_AGENT_KINDS)[number]

export const COMPOSER_PASTE_KINDS = ['text', 'files'] as const
export type ComposerPasteKind = (typeof COMPOSER_PASTE_KINDS)[number]

/** The new-session destination. Repository names are never logged. */
export const COMPOSER_TARGET_KINDS = ['unselected', 'repository', 'chat'] as const
export type ComposerTargetKind = (typeof COMPOSER_TARGET_KINDS)[number]

/** Environment picker groups. Specific environment ids are never logged. */
export const COMPOSER_ENVIRONMENT_KINDS = ['cloud', 'sandbox', 'ahp'] as const
export type ComposerEnvironmentKind = (typeof COMPOSER_ENVIRONMENT_KINDS)[number]

/** `session` is the session approval mode picker; `mcp_permission` is the MCP tool permission selector. */
export const COMPOSER_APPROVAL_KINDS = ['session', 'mcp_permission'] as const
export type ComposerApprovalKind = (typeof COMPOSER_APPROVAL_KINDS)[number]

export const COMPOSER_SCOPE_KINDS = ['personal', 'organization', 'unknown'] as const
export type ComposerScopeKind = (typeof COMPOSER_SCOPE_KINDS)[number]

/**
 * `not_ready`: the destination could not accept a submit yet (quota, policy, missing repository or environment).
 * `rejected`: the submit was declined after it started (scope changed, the handoff reported a failure).
 * `validation` / `error`: the submit threw a validation or unexpected error.
 */
export const COMPOSER_SUBMIT_BLOCKED_REASONS = ['not_ready', 'rejected', 'validation', 'error'] as const
export type ComposerSubmitBlockedReason = (typeof COMPOSER_SUBMIT_BLOCKED_REASONS)[number]

/** What the submit created: a Chat session or a coding task on a repository. */
export const COMPOSER_SUBMIT_TARGETS = ['chat', 'task'] as const
export type ComposerSubmitTarget = (typeof COMPOSER_SUBMIT_TARGETS)[number]

/** Inclusive upper bounds for length buckets. Text is never sent, only its bucket. */
export const LENGTH_BUCKETS = [
  {max: 0, label: '0'},
  {max: 20, label: '1-20'},
  {max: 100, label: '21-100'},
  {max: 500, label: '101-500'},
  {max: 2000, label: '501-2000'},
  {max: Number.POSITIVE_INFINITY, label: '2001+'},
] as const
export type LengthBucket = (typeof LENGTH_BUCKETS)[number]['label']
